<script lang="ts">
  // PageHost — `#/host` (CPU + MEM cards + Disk section + footer).
  //
  // Real data only — every value derives from the MetricSnapshot.
  // The disk section is capped at three mounts. The Network section,
  // fabricated uptime footer and "cores"/"pps" placeholders were
  // removed in the panel-focus pass: they rendered data that does
  // not exist on the wire DTO.

  import { onDestroy } from "svelte";
  import { createWsClient, type WsClient, type SnapshotState } from "../lib/ws";
  import {
    formatBytes,
    formatNumber,
    formatPercent,
  } from "../lib/format";

  const isBrowser = typeof window !== "undefined";
  const client: WsClient | null = isBrowser ? createWsClient() : null;
  if (client) client.connect();

  function emptySnapshot(): SnapshotState {
    return {
      host: null,
      containers: [],
      logLines: [],
      connectionState: "disconnected",
      lastError: null,
      lastUpdateMs: null,
      hostHistory: [],
      ramHistory: [],
      cpuHistory: {},
    };
  }

  let snapshot: SnapshotState = client ? initialFrom(client) : emptySnapshot();
  let unsub: (() => void) | null = null;

  function initialFrom(c: WsClient): SnapshotState {
    let v: SnapshotState = emptySnapshot();
    const u = c.snapshot.subscribe((next) => {
      v = next;
    });
    u();
    return v;
  }

  if (client) {
    unsub = client.snapshot.subscribe((next) => {
      snapshot = next;
    });
  }

  onDestroy(() => unsub?.());

  // --- derivations -------------------------------------------------------

  $: host = snapshot.host;
  $: containers = snapshot.containers ?? [];

  $: cpuPct = host ? Math.max(0, Math.min(100, Math.round(host.cpu_pct))) : 0;
  $: cpuLabel = host ? formatPercent(cpuPct) : "—";

  $: ramPct =
    host && host.ram_total > 0
      ? Math.max(0, Math.min(100, Math.round((host.ram_used / host.ram_total) * 100)))
      : 0;
  $: ramLabel = host ? formatPercent(ramPct) : "—";
  $: ramUsedLabel = host ? formatBytes(host.ram_used) : "—";
  $: ramTotalLabel = host ? formatBytes(host.ram_total) : "—";

  // Disk section — cap to 3 mounts per spec scenario 2.
  const DISK_CAP = 3;
  $: disks = host ? host.disks.slice(0, DISK_CAP) : [];

  // Footer — container count + average CPU.
  $: containerCount = formatNumber(containers.length);
  $: cpuAvgLabel = formatPercent(cpuPct);

  function diskPct(used: number, total: number): number {
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
  }

</script>

<section class="page-host" aria-labelledby="host-heading">
  <h2 id="host-heading" class="page-host__title">Host metrics</h2>

  <div class="page-host__cards">
    <article class="host-card" data-testid="cpu-card">
      <header class="host-card__head">
        <span class="host-card__label">CPU</span>
        <span class="host-card__value">{cpuLabel}</span>
      </header>
      <div class="progress" role="progressbar" aria-valuenow={cpuPct} aria-valuemin="0" aria-valuemax="100" aria-label="CPU usage">
        <span class="progress__fill" style="width: {cpuPct}%"></span>
      </div>
      <footer class="host-card__foot">
        <span class="host-card__foot-item">
          <span class="host-card__foot-label">load1</span>
          <span class="host-card__foot-value">{host ? host.load1.toFixed(2) : "—"}</span>
        </span>
      </footer>
    </article>

    <article class="host-card" data-testid="mem-card">
      <header class="host-card__head">
        <span class="host-card__label">RAM</span>
        <span class="host-card__value">{ramLabel}</span>
      </header>
      <div class="progress" role="progressbar" aria-valuenow={ramPct} aria-valuemin="0" aria-valuemax="100" aria-label="Memory usage">
        <span class="progress__fill" style="width: {ramPct}%"></span>
      </div>
      <footer class="host-card__foot">
        <span class="host-card__foot-item">
          <span class="host-card__foot-label">used</span>
          <span class="host-card__foot-value">{ramUsedLabel}</span>
        </span>
        <span class="host-card__foot-item">
          <span class="host-card__foot-label">total</span>
          <span class="host-card__foot-value">{ramTotalLabel}</span>
        </span>
      </footer>
    </article>
  </div>

  <section class="page-host__section" aria-labelledby="disk-heading" data-testid="disk-section">
    <h3 id="disk-heading" class="page-host__section-title">Disk</h3>
    {#if disks.length === 0}
      <p class="page-host__empty">No disk metrics reported</p>
    {:else}
      <div class="disk-grid">
        {#each disks as d (d.mount)}
          <article class="disk-card" data-testid="disk-card" data-mount={d.mount}>
            <header class="disk-card__head">
              <span class="disk-card__mount">{d.mount}</span>
              <span class="disk-card__value">{formatPercent(diskPct(d.used, d.total))}</span>
            </header>
            <div class="progress" role="progressbar" aria-valuenow={diskPct(d.used, d.total)} aria-valuemin="0" aria-valuemax="100" aria-label={`Disk usage on ${d.mount}`}>
              <span class="progress__fill" style="width: {diskPct(d.used, d.total)}%"></span>
            </div>
            <footer class="disk-card__foot">
              <span>{formatBytes(d.used)} used</span>
              <span>{formatBytes(d.total)} total</span>
            </footer>
          </article>
        {/each}
      </div>
    {/if}
  </section>

  <footer class="page-host__footer" data-testid="host-footer">
    <span class="page-host__footer-item">
      <span class="page-host__footer-label">containers</span>
      <span class="page-host__footer-value">{containerCount}</span>
    </span>
    <span class="page-host__footer-item">
      <span class="page-host__footer-label">CPU avg</span>
      <span class="page-host__footer-value">{cpuAvgLabel}</span>
    </span>
  </footer>
</section>
